// ============================================================
// frontend/src/views/LoginView.tsx
// ============================================================
// Login + signup screen.
//
// Multi-tenant flow:
//   - Anyone can sign up at /api/auth/register
//   - New signups land as 'pending' and CANNOT log in until a super
//     admin approves them. We surface that in the success state.
//   - Login surfaces a friendly error for pending/rejected/suspended
//     accounts (the server sends the message).
// ============================================================

import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useSaving } from '../lib/useSaving';
import { ConsiliumMark } from '../components/Brand';

export function LoginView() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [pendingMsg, setPendingMsg] = useState('');
  const { saving, saved, run } = useSaving();

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMsg('');
    setPendingMsg('');
    try {
      await run(async () => {
        if (mode === 'register') {
          const result = await register(email, password, name);
          // Success — but the user is NOT logged in. Show the message
          // and reset the form so they can come back to log in later.
          setPendingMsg(result.message);
          setEmail('');
          setPassword('');
          setName('');
          setMode('login');
        } else {
          await login(email, password, rememberMe);
        }
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  const switchMode = (next: 'login' | 'register') => {
    if (next === mode) return;
    setMode(next);
    setErrorMsg('');
    setPendingMsg('');
  };

  const disabled = !email || !password || (mode === 'register' && !name);
  const submitLabel = saving
    ? mode === 'register' ? 'Submitting…' : 'Signing you in…'
    : saved
      ? mode === 'register' ? 'Submitted' : 'Welcome'
      : mode === 'register' ? 'Request access' : 'Sign in';

  return (
    <div className="login-shell">
      <div className="login-orb login-orb-1" aria-hidden="true" />
      <div className="login-orb login-orb-2" aria-hidden="true" />
      <div className="login-orb login-orb-3" aria-hidden="true" />

      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-mark">
            <ConsiliumMark size={56} animate ariaLabel="Consilium" />
          </div>
          <div className="login-brand-name" aria-hidden="true">Consilium</div>
          <div className="login-rule" aria-hidden="true" />
          <div className="login-tagline">
            {mode === 'register'
              ? 'A calm, considered home for your practice.'
              : 'Sign in to your practice.'}
          </div>
        </div>

        <div className="login-segment" role="tablist" aria-label="Authentication mode">
          <div
            className={`login-segment-indicator${mode === 'register' ? ' is-right' : ''}`}
            aria-hidden="true"
          />
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={`login-segment-btn${mode === 'login' ? ' is-active' : ''}`}
            onClick={() => switchMode('login')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={`login-segment-btn${mode === 'register' ? ' is-active' : ''}`}
            onClick={() => switchMode('register')}
          >
            Request access
          </button>
        </div>

        <form onSubmit={onSubmit} className="stack-lg login-form" key={mode}>
          {mode === 'register' && (
            <div className="float-field">
              <input
                id="lf-name"
                className="input"
                placeholder=" "
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
              <label className="float-field-label" htmlFor="lf-name">Your name</label>
            </div>
          )}

          <div className="float-field">
            <input
              id="lf-email"
              type="email"
              className="input"
              placeholder=" "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus={mode === 'login'}
              required
            />
            <label className="float-field-label" htmlFor="lf-email">Email address</label>
          </div>

          <div className="float-field">
            <input
              id="lf-password"
              type="password"
              className="input"
              placeholder=" "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              required
              minLength={mode === 'register' ? 8 : undefined}
            />
            <label className="float-field-label" htmlFor="lf-password">
              {mode === 'register' ? 'Password (8+ characters)' : 'Password'}
            </label>
          </div>

          {mode === 'login' && (
            <label className="login-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Keep me signed in</span>
            </label>
          )}

          {pendingMsg && <div className="login-alert is-success">{pendingMsg}</div>}
          {errorMsg && <div className="login-alert is-error">{errorMsg}</div>}

          <button
            type="submit"
            className={`btn-premium${saved ? ' is-saved' : ''}`}
            disabled={disabled || saving}
          >
            {saving && <span className="spin" aria-hidden="true" />}
            {submitLabel}
          </button>
        </form>

        <div className="login-foot">
          {mode === 'login'
            ? 'New practice? Request access — accounts are reviewed individually.'
            : "We'll email you once your account is approved."}
        </div>
      </div>
    </div>
  );
}
